import React, { useEffect, useMemo, useState } from "react"
import Button from "../../Form/Button"
import FormGroup from "../../Form/FormGroup"
import FormGroupInline from "../../Form/FormGroupInline"
import Select from "../../Form/Select"
import { listAssignTo } from "../../../schemas/Chat"
import { useData } from "../../../contexts/dataContext"
import { useWebSocketChatContext } from "../../../contexts/webSocketChatContext"
import api from "../../../services/api"
import { useAuth } from "../../../contexts/authContext"
import styles from "./modalOutboundCampaign.module.css"
import { getUsersBySelectedTeams } from "../../../utils/utils"

const existingConversationOptions = [
    { id: "keep_assigned", value: "Manter quem já é responsável" },
    { id: "replace", value: "Substituir pelo responsável da campanha" },
]
const roundRobinModeOptions = [
    { id: "roles", value: "Padrão pelos cargos com rodízio ativo" },
    { id: "custom", value: "Usuários específicos" },
]

const normalizeLabel = value => `${value || ""}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "")
export const normalizeCampaignPhone = value => {
    let digits = `${value || ""}`.replace(/\D/g, "")
    if (digits.startsWith("00")) digits = digits.slice(2)
    if ([10, 11].includes(digits.length)) digits = `55${digits}`
    return digits
}

const findField = (fields, candidates) => fields.find(field => candidates.some(candidate => normalizeLabel(field.value).includes(candidate)))?.id

const ConfigContacts = ({ data = {}, fields = [], rows = [], handleChangeValue = () => {}, handleChangeStep = () => {} }) => {
    const [aiAgents, setAiAgents] = useState([])
    const { permissions } = useAuth()
    const canReplaceExistingOwner = Number(permissions?.chat || 0) > 1
    const { teams = [], roles = [], users = [] } = useData()
    const { chatInboxes = [] } = useWebSocketChatContext()
    const selectedInbox = chatInboxes.find(inbox => inbox?._id === data?.inbox_id)
    const allowedTeamIds = selectedInbox?.teams?.map(team => `${team?.team_id}`) || []
    const allowedUserIds = selectedInbox?.users?.map(user => `${user?.user_id}`) || []
    const availableTeams = allowedTeamIds.length ? teams.filter(team => allowedTeamIds.includes(`${team.id}`)) : teams
    const teamUsers = getUsersBySelectedTeams(allowedTeamIds, users, teams)
    const availableUsers = allowedUserIds.length ? teamUsers.filter(user => allowedUserIds.includes(`${user.id}`)) : teamUsers
    const sampleRow = rows?.[0] || {}
    const assignmentModes = useMemo(() => [
        { id: "inbox_default", value: "Usar regra padrão da caixa" },
        ...listAssignTo,
        ...(aiAgents.length ? [{ id: "ai_agent", value: "Agente de IA" }] : []),
    ], [aiAgents.length])

    useEffect(() => {
        let mounted = true
        api.get("/outbound-campaigns/assignment-options")
            .then(response => {
                if (!mounted) return
                setAiAgents((response?.data?.data?.ai_agents || []).map(agent => ({ id: agent._id, value: agent.name || "Agente de IA" })))
            })
            .catch(() => {
                if (mounted) setAiAgents([])
            })
        return () => { mounted = false }
    }, [])

    useEffect(() => {
        if (!data?.contact_name) {
            const field = findField(fields, ["nome", "name", "contato", "aluno", "cliente"])
            if (field) handleChangeValue("contact_name", field)
        }
        if (!data?.contact_phone) {
            const field = findField(fields, ["celular", "telefone", "phone", "whatsapp", "fone"])
            if (field) handleChangeValue("contact_phone", field)
        }
        if (!data?.assignment_mode) handleChangeValue("assignment_mode", "inbox_default")
        if (!data?.existing_conversations) handleChangeValue("existing_conversations", "keep_assigned")
        if (!canReplaceExistingOwner && data?.existing_conversations === "replace") handleChangeValue("existing_conversations", "keep_assigned")
    }, [canReplaceExistingOwner, data?.assignment_mode, data?.contact_name, data?.contact_phone, data?.existing_conversations, fields])

    const contactSummary = useMemo(() => {
        const seen = new Set()
        let valid = 0
        let invalid = 0
        let duplicates = 0
        rows.forEach(row => {
            const phone = normalizeCampaignPhone(row?.[data?.contact_phone])
            if (phone.length < 10 || phone.length > 15) {
                invalid += 1
                return
            }
            if (seen.has(phone)) {
                duplicates += 1
                return
            }
            seen.add(phone)
            valid += 1
        })
        return { total: rows.length, valid, invalid, duplicates }
    }, [data?.contact_phone, rows])

    const handleChangeAssignmentUsers = value => {
        handleChangeValue("assignment_users", value)
        if (!value?.includes("auto")) handleChangeValue("assignment_round_robin", { mode: "roles", users: [] })
    }

    const handleChangeRoundRobin = (name, value) => {
        handleChangeValue("assignment_round_robin", {
            mode: "roles",
            users: [],
            ...data?.assignment_round_robin,
            [name]: value,
            ...(name === "mode" && value !== "custom" ? { users: [] } : {}),
        })
    }

    const validateAndContinue = () => {
        if (!data?.contact_name) return handleChangeValue("contact_error", "Selecione a coluna que contém o nome.")
        if (!data?.contact_phone) return handleChangeValue("contact_error", "Selecione a coluna que contém o celular.")
        if (!contactSummary.valid) return handleChangeValue("contact_error", "Nenhum celular válido foi encontrado nessa coluna.")
        if (data?.assignment_mode === "users" && !data?.assignment_users?.length) return handleChangeValue("contact_error", "Selecione ao menos um usuário responsável.")
        if (data?.assignment_mode === "users" && data?.assignment_users?.includes("auto") && data?.assignment_round_robin?.mode === "custom" && !data?.assignment_round_robin?.users?.length) return handleChangeValue("contact_error", "Selecione os usuários do rodízio personalizado.")
        if (data?.assignment_mode === "teamAndRole" && !data?.assignment_team_id && !data?.assignment_role_id) return handleChangeValue("contact_error", "Selecione uma equipe e/ou cargo.")
        if (data?.assignment_mode === "ai_agent" && !data?.assignment_agent_id) return handleChangeValue("contact_error", "Selecione um agente de IA ativo.")
        handleChangeValue("contact_error", "")
        handleChangeStep(4)
    }

    return (
        <>
            <div className={styles.stepIntro}>
                <strong>Identificação dos contatos</strong>
                <span>O celular identifica o destinatário. O nome será usado ao criar contatos novos; nomes de contatos existentes não serão alterados.</span>
            </div>
            <div className={styles.mappingGrid}>
                <FormGroup labelText="Coluna com o nome">
                    <Select multiple={false} name="Coluna com o nome" placeholder="Selecione uma coluna" options={fields} selected={data?.contact_name ?? ""} onChange={value => handleChangeValue("contact_name", value)} />
                    <small className={styles.fieldSample} aria-hidden={!data?.contact_name}>{data?.contact_name ? `Exemplo: ${sampleRow?.[data.contact_name] || "sem valor na primeira linha"}` : "\u00a0"}</small>
                </FormGroup>
                <FormGroup labelText="Coluna com o celular">
                    <Select multiple={false} name="Coluna com o celular" placeholder="Selecione uma coluna" options={fields} selected={data?.contact_phone ?? ""} onChange={value => handleChangeValue("contact_phone", value)} />
                    <small className={styles.fieldSample} aria-hidden={!data?.contact_phone}>{data?.contact_phone ? `Exemplo: ${sampleRow?.[data.contact_phone] || "sem valor na primeira linha"}` : "\u00a0"}</small>
                </FormGroup>
            </div>
            {data?.contact_phone && <div className={styles.contactSummary}>
                <div><span>Linhas</span><strong>{contactSummary.total}</strong></div>
                <div><span>Envios únicos</span><strong>{contactSummary.valid}</strong></div>
                <div><span>Duplicados</span><strong>{contactSummary.duplicates}</strong></div>
                <div><span>Inválidos</span><strong>{contactSummary.invalid}</strong></div>
            </div>}
            <div className={styles.stepIntro}>
                <strong>Responsáveis pelas conversas</strong>
                <span>Esta configuração vale somente para a campanha e não altera a regra padrão da caixa de entrada.</span>
            </div>
            <FormGroup labelText="Atribuir para">
                <Select name="Responsável" placeholder="Selecione" options={assignmentModes} selected={data?.assignment_mode || "inbox_default"} multiple={false} onChange={assignment_mode => handleChangeValue("assignment_mode", assignment_mode)} />
            </FormGroup>
            {data?.assignment_mode === "users" && <>
                <FormGroup labelText="Usuários">
                    <Select name="Usuários" placeholder="Selecione um ou mais usuários" options={[{ id: "auto", value: "Rodízio automático" }, ...availableUsers]} selected={data?.assignment_users || []} multiple={true} onChange={handleChangeAssignmentUsers} />
                </FormGroup>
                {data?.assignment_users?.includes("auto") && <>
                    <FormGroup labelText="Base do rodízio automático">
                        <Select name="Base do rodízio" placeholder="Selecione" options={roundRobinModeOptions} selected={data?.assignment_round_robin?.mode || "roles"} multiple={false} onChange={value => handleChangeRoundRobin("mode", value)} />
                    </FormGroup>
                    {data?.assignment_round_robin?.mode === "custom" && <FormGroup labelText="Usuários do rodízio personalizado">
                        <Select name="Usuários do rodízio" placeholder="Selecione os participantes" options={availableUsers} selected={data?.assignment_round_robin?.users || []} multiple={true} onChange={value => handleChangeRoundRobin("users", value)} />
                    </FormGroup>}
                </>}
            </>}
            {data?.assignment_mode === "teamAndRole" && <div className={styles.mappingGrid}>
                <FormGroup labelText="Equipe"><Select name="Equipe" placeholder="Selecione" options={availableTeams} selected={data?.assignment_team_id || ""} multiple={false} onChange={value => handleChangeValue("assignment_team_id", value)} /></FormGroup>
                <FormGroup labelText="Cargo"><Select name="Cargo" placeholder="Selecione" options={roles} selected={data?.assignment_role_id || ""} multiple={false} onChange={value => handleChangeValue("assignment_role_id", value)} /></FormGroup>
            </div>}
            {data?.assignment_mode === "ai_agent" && <FormGroup labelText="Agente de IA">
                <Select name="Agente de IA" placeholder="Selecione um agente ativo" options={aiAgents} selected={data?.assignment_agent_id || ""} multiple={false} onChange={value => {
                    handleChangeValue("assignment_agent_id", value)
                    handleChangeValue("assignment_agent_name", aiAgents.find(agent => agent.id === value)?.value || "Agente de IA")
                }} />
                <small className={styles.fieldSample}>O agente será ativado sem mensagem adicional e assumirá o fluxo quando o contato responder ao template.</small>
            </FormGroup>}
            {data?.assignment_mode !== "inbox_default" && <FormGroup labelText="Quando a conversa já existir">
                <Select name="Conversas existentes" options={canReplaceExistingOwner ? existingConversationOptions : existingConversationOptions.filter(option => option.id !== "replace")} selected={data?.existing_conversations || "keep_assigned"} multiple={false} onChange={value => handleChangeValue("existing_conversations", value)} />
                <small className={styles.fieldSample}>{data?.existing_conversations === "replace" ? "O responsável atual será substituído." : "Conversas já atribuídas serão preservadas; novas ou sem responsável receberão a atribuição da campanha."}</small>
            </FormGroup>}
            {data?.contact_error && <div className={styles.validationNotice}>{data.contact_error}</div>}
            <FormGroupInline>
                <Button variant="secondary" onClick={() => handleChangeStep(2)}>Voltar para origem</Button>
                <Button variant="primary" onClick={validateAndContinue}>Configurar mensagem</Button>
            </FormGroupInline>
        </>
    )
}

export default ConfigContacts
